import { useI18n } from '@i18n/I18nProvider'
import { type CameraProblem, cameraProblem, openRearCamera, stopCamera } from '@lib/camera'
import { createQrReader, type QrReader } from '@lib/qr-reader'
import { normalizeTreeCode, treeCodeFromScan } from '@lib/tree-code'
import { Button } from '@ui/Button'
import { Icon } from '@ui/Icon'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'wouter'

type Status = 'starting' | 'scanning' | CameraProblem

type ScanPageProps = {
	openCamera?: () => Promise<MediaStream>
	createReader?: () => Promise<QrReader>
	intervalMs?: number
}

const CORNERS = [
	'top-0 left-0 border-t-4 border-l-4',
	'top-0 right-0 border-t-4 border-r-4',
	'bottom-0 left-0 border-b-4 border-l-4',
	'bottom-0 right-0 border-b-4 border-r-4',
]

// "Escanear árbol" (mockup): la cámara a pantalla completa lee el QR que hay junto al árbol y
// abre su ficha (`/a/<código>`), igual que si se escanease con la cámara del móvil. Si no hay
// cámara o permiso, siempre queda escribir el código a mano. Cámara y lector se inyectan en
// los tests.
export const ScanPage = ({
	openCamera = openRearCamera,
	createReader = createQrReader,
	intervalMs = 250,
}: ScanPageProps) => {
	const { t } = useI18n()
	const [, navigate] = useLocation()
	const navigateRef = useRef(navigate)
	navigateRef.current = navigate
	const video = useRef<HTMLVideoElement>(null)
	const [status, setStatus] = useState<Status>('starting')
	const [notATree, setNotATree] = useState(false)
	const [typed, setTyped] = useState('')
	const [invalid, setInvalid] = useState(false)

	useEffect(() => {
		let active = true
		let stream: MediaStream | null = null
		let timer: ReturnType<typeof setInterval> | undefined
		const start = async () => {
			try {
				stream = await openCamera()
				const read = await createReader()
				// Se salió de la pantalla mientras se abría: la limpieza ya pasó, se apaga aquí.
				if (!active) return stopCamera(stream)
				const element = video.current
				if (element) {
					element.srcObject = stream
					element.play?.()?.catch(() => undefined) // sin esperar: autoPlay ya lo arranca
				}
				setStatus('scanning')
				let busy = false
				timer = setInterval(async () => {
					if (busy || !video.current) return
					busy = true
					try {
						const text = await read(video.current)
						if (!active || text === null) return
						const code = treeCodeFromScan(text)
						if (code) {
							active = false
							clearInterval(timer)
							stopCamera(stream) // se apaga ya, sin esperar a cambiar de pantalla
							stream = null
							navigateRef.current(`/a/${code}`)
						} else setNotATree(true)
					} catch {
						// Fotograma que el lector no pudo analizar: se prueba con el siguiente.
					} finally {
						busy = false
					}
				}, intervalMs)
			} catch (error) {
				stopCamera(stream) // la cámara pudo abrirse y fallar después (el lector no cargó)
				stream = null
				if (active) setStatus(cameraProblem(error))
			}
		}
		start()
		return () => {
			active = false
			clearInterval(timer)
			stopCamera(stream)
		}
	}, [openCamera, createReader, intervalMs])

	const submit = (event: FormEvent) => {
		event.preventDefault()
		const code = normalizeTreeCode(typed)
		if (code) navigate(`/a/${code}`)
		else setInvalid(true)
	}

	const problem = status === 'denied' || status === 'unavailable'
	const message = status === 'starting' ? t('scan.starting') : notATree ? t('scan.notATree') : t('scan.hint')

	return (
		<main aria-labelledby="scan-title" className="fixed inset-0 z-50 flex flex-col bg-stone-950 text-white">
			<div className="flex items-center justify-between gap-2 px-4 pt-[calc(0.5rem+env(safe-area-inset-top))] pb-2">
				<h1 id="scan-title" className="text-lg font-semibold">
					{t('scan.title')}
				</h1>
				<Link
					href="/"
					aria-label={t('scan.close')}
					className="flex size-12 shrink-0 items-center justify-center rounded-full text-white/90 active:bg-white/10"
				>
					<Icon name="close" className="size-7" />
				</Link>
			</div>

			<div className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-6 overflow-hidden p-6">
				<video
					ref={video}
					muted
					playsInline
					autoPlay
					aria-hidden="true"
					className={`absolute inset-0 size-full object-cover ${status === 'scanning' ? '' : 'hidden'}`}
				/>
				{problem ? (
					<p role="alert" className="relative max-w-sm text-center text-lg">
						{t(status === 'denied' ? 'scan.denied' : 'scan.unavailable')}
					</p>
				) : (
					<>
						<div className="relative size-56 rounded-2xl border-4 border-white/30" aria-hidden="true">
							{CORNERS.map((corner) => (
								<span key={corner} className={`absolute size-8 rounded-sm border-ochre ${corner}`} />
							))}
						</div>
						<p
							role="status"
							className="relative max-w-xs rounded-xl bg-black/60 px-4 py-2 text-center text-lg"
						>
							{message}
						</p>
					</>
				)}
			</div>

			<form
				onSubmit={submit}
				noValidate
				className="flex flex-col gap-3 rounded-t-3xl bg-earth-50 px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] text-forest-900"
			>
				<p className="font-semibold">{t('scan.manual')}</p>
				<label className="flex flex-col gap-1">
					<span className="text-base">{t('scan.codeLabel')}</span>
					<input
						value={typed}
						onChange={(event) => {
							setTyped(event.target.value)
							setInvalid(false)
						}}
						placeholder="VC-0123"
						autoCapitalize="characters"
						autoComplete="off"
						spellCheck={false}
						aria-invalid={invalid}
						aria-describedby={invalid ? 'scan-code-error' : undefined}
						className="min-h-12 rounded-xl border-2 border-earth-300 bg-white px-4 text-lg uppercase text-forest-900 focus:border-forest-700"
					/>
				</label>
				{invalid && (
					<p id="scan-code-error" role="alert" className="text-base text-damaged">
						{t('scan.invalidCode')}
					</p>
				)}
				<Button type="submit" fullWidth>
					{t('scan.go')}
				</Button>
			</form>
		</main>
	)
}
